import React, {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { createPortal } from "react-dom";

import {
  ACCOUNT_COLUMN_LABELS,
  accountCoverageCompleteness,
  getAccountColumnPeople,
  teamViewsAvatarColor,
} from "./teamViewsHelpers";
import type { AccountColumnPerson } from "./teamViewsHelpers";
import type {
  TeamViewsAccountRow,
  TeamViewsPerson,
} from "../../../Store/Reducers/TeamViewsSlice";
import { getInitials } from "../../../Utils/Helper/Helper";
import {
  ACCOUNT_TEAM_AVATAR_BG,
  EXEC_SPONSOR_AVATAR_BG,
} from "../../../Utils/Helper/profileAvatarUi";

type TeamViewsAccountBoardProps = {
  accounts: TeamViewsAccountRow[];
  columns: string[];
  /** When set (Show my team), hide this user so they are not repeated on every account. */
  excludeUserId?: string | null;
  resolvePerson: (userId?: string | null) => TeamViewsPerson | undefined;
  onSelectPerson: (userId?: string | null) => void;
  onSelectAccount: (accountId?: string | null) => void;
};

const EXEC_COLUMNS = new Set(["SPONSOR", "NON_SPONSOR_EXEC", "EXTRA_SPONSOR"]);
const POP_MARGIN = 8;
const POP_ESTIMATED_WIDTH = 280;

function clampPopoverPosition(
  preferred: { top: number; left: number },
  size: { width: number; height: number },
  trigger: DOMRect
): { top: number; left: number } {
  const maxLeft = Math.max(
    POP_MARGIN,
    window.innerWidth - size.width - POP_MARGIN
  );
  const left = Math.min(Math.max(preferred.left, POP_MARGIN), maxLeft);
  let top = preferred.top;
  if (top + size.height > window.innerHeight - POP_MARGIN) {
    top = Math.max(POP_MARGIN, trigger.top - size.height - 6);
  }
  return { top, left };
}

type SlotOverflowProps = {
  people: AccountColumnPerson[];
  roleLabel: string;
  resolvePerson: (userId?: string | null) => TeamViewsPerson | undefined;
  onSelectPerson: (userId?: string | null) => void;
  avatarBg: (person: AccountColumnPerson) => string;
};

/** Clickable +N that lists overflow people in a viewport-clamped popover. */
const SlotOverflowButton: React.FC<SlotOverflowProps> = ({
  people,
  roleLabel,
  resolvePerson,
  onSelectPerson,
  avatarBg,
}) => {
  const [popAt, setPopAt] = useState<{ top: number; left: number } | null>(
    null
  );
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const popRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!popAt) return undefined;
    const close = () => setPopAt(null);
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") close();
    };
    const onPointerDown = (e: MouseEvent) => {
      const target = e.target as Node;
      if (triggerRef.current?.contains(target)) return;
      if (popRef.current?.contains(target)) return;
      close();
    };
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    window.addEventListener("scroll", close, true);
    window.addEventListener("resize", close);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("scroll", close, true);
      window.removeEventListener("resize", close);
    };
  }, [popAt]);

  useLayoutEffect(() => {
    if (!popAt || !popRef.current || !triggerRef.current) return;
    const popRect = popRef.current.getBoundingClientRect();
    const triggerRect = triggerRef.current.getBoundingClientRect();
    const next = clampPopoverPosition(
      popAt,
      { width: popRect.width, height: popRect.height },
      triggerRect
    );
    if (next.left !== popAt.left || next.top !== popAt.top) {
      setPopAt(next);
    }
  }, [popAt]);

  const openPop = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (popAt) {
      setPopAt(null);
      return;
    }
    const rect = triggerRef.current?.getBoundingClientRect();
    if (!rect) return;
    setPopAt(
      clampPopoverPosition(
        { top: rect.bottom + 6, left: rect.left },
        { width: POP_ESTIMATED_WIDTH, height: 120 },
        rect
      )
    );
  };

  const popover = popAt
    ? createPortal(
        <div
          ref={popRef}
          className="rl-prototype-skin tv-cell-pop"
          style={{ top: popAt.top, left: popAt.left }}
          role="dialog"
          aria-label={`${roleLabel} on this account`}
        >
          <div className="tv-cell-pop-hdr">{roleLabel}</div>
          <div className="tv-by-account-chips tv-cell-pop-list">
            {people.map(person => {
              const roster = resolvePerson(person.user_id);
              return (
                <button
                  key={
                    person.user_id ? String(person.user_id) : person.full_name
                  }
                  type="button"
                  className="tv-by-account-chip"
                  title={
                    roster
                      ? `View accounts for ${person.full_name}`
                      : person.full_name
                  }
                  disabled={!roster}
                  onClick={() => {
                    setPopAt(null);
                    onSelectPerson(person.user_id);
                  }}
                >
                  <span
                    className="tv-cell-avatar"
                    style={{ background: avatarBg(person) }}
                    aria-hidden
                  >
                    {getInitials(person.full_name)}
                  </span>
                  <span className="tv-by-account-name">{person.full_name}</span>
                </button>
              );
            })}
          </div>
        </div>,
        document.body
      )
    : null;

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        className="tv-acct-slot-more"
        aria-expanded={Boolean(popAt)}
        title={people.map(p => p.full_name).join(", ")}
        onClick={openPop}
      >
        +{people.length}
      </button>
      {popover}
    </>
  );
};

/**
 * V3 By Account cards mode — coverage board instead of a spreadsheet table.
 * Each account is a visual map of role slots; empty slots read as gaps.
 */
const TeamViewsAccountBoard: React.FC<TeamViewsAccountBoardProps> = ({
  accounts,
  columns,
  excludeUserId = null,
  resolvePerson,
  onSelectPerson,
  onSelectAccount,
}) => {
  const [allExpanded, setAllExpanded] = useState(false);

  const cards = useMemo(
    () =>
      accounts.map(row => {
        const { filled, total } = accountCoverageCompleteness(row, columns);
        const pct = total > 0 ? Math.round((filled / total) * 100) : 0;
        return { row, filled, total, pct };
      }),
    [accounts, columns]
  );

  return (
    <div className="tv-acct-board" role="list" aria-label="Accounts coverage">
      {cards.map(({ row, filled, total, pct }) => {
        const isComplete = total > 0 && filled === total;
        const accountLabel = row.account_name || "Unnamed account";
        return (
          <article
            key={row.account_id}
            className={`tv-acct-card${allExpanded ? " is-open" : ""}`}
            role="listitem"
          >
            <div
              className="tv-acct-card-hdr"
              role="button"
              tabIndex={0}
              aria-expanded={allExpanded}
              onClick={() => setAllExpanded(open => !open)}
              onKeyDown={e => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  setAllExpanded(open => !open);
                }
              }}
            >
              <span
                className={`tv-acct-completeness${isComplete ? " is-complete" : ""}`}
                style={
                  isComplete
                    ? undefined
                    : ({ "--tv-complete": `${pct}%` } as React.CSSProperties)
                }
                title={`${filled} of ${total} roles filled`}
                aria-hidden
              />
              <span className="tv-acct-card-name">
                <button
                  type="button"
                  className="tv-acct-card-name--link"
                  title={`Open relationships for ${accountLabel}`}
                  onClick={e => {
                    e.stopPropagation();
                    onSelectAccount(row.account_id);
                  }}
                >
                  {accountLabel}
                </button>
              </span>
              <span className="tv-acct-card-score">
                {filled}/{total}
              </span>
              <span className="tv-acct-card-chevron" aria-hidden>
                {allExpanded ? "▾" : "▸"}
              </span>
            </div>

            <div className="tv-acct-slots">
              {columns.map(columnKey => {
                const label = ACCOUNT_COLUMN_LABELS[columnKey] || columnKey;
                const people = getAccountColumnPeople(
                  row,
                  columnKey,
                  excludeUserId
                );
                const empty = people.length === 0;
                // Always two faces + optional +N (stable columns); names stack vertically.
                const visible = people.slice(0, 2);
                const overflow = people.slice(2);

                const avatarBg = (person: AccountColumnPerson): string => {
                  const roster = resolvePerson(person.user_id);
                  if (roster) return teamViewsAvatarColor(roster);
                  return EXEC_COLUMNS.has(columnKey)
                    ? EXEC_SPONSOR_AVATAR_BG
                    : ACCOUNT_TEAM_AVATAR_BG;
                };

                return (
                  <div
                    key={columnKey}
                    className={`tv-acct-slot${empty ? " is-empty" : ""}`}
                  >
                    <span className="tv-acct-slot-role">{label}</span>
                    {empty ? (
                      <span className="tv-acct-slot-gap">Open</span>
                    ) : (
                      <div className="tv-acct-slot-people tv-acct-slot-people--named">
                        {visible.map((person, index) => {
                          const roster = resolvePerson(person.user_id);
                          const personKey = person.user_id
                            ? String(person.user_id)
                            : person.full_name;
                          const isLast = index === visible.length - 1;
                          const personBtn = (
                            <button
                              type="button"
                              className="tv-acct-slot-person"
                              disabled={!roster}
                              title={person.full_name}
                              onClick={e => {
                                e.stopPropagation();
                                onSelectPerson(person.user_id);
                              }}
                            >
                              <span
                                className="tv-cell-avatar"
                                style={{ background: avatarBg(person) }}
                                aria-hidden
                              >
                                {getInitials(person.full_name)}
                              </span>
                              <span className="tv-acct-slot-person-name">
                                {person.full_name}
                              </span>
                            </button>
                          );

                          if (isLast && overflow.length) {
                            return (
                              <div
                                key={personKey}
                                className="tv-acct-slot-person-row"
                              >
                                {personBtn}
                                <SlotOverflowButton
                                  people={overflow}
                                  roleLabel={label}
                                  resolvePerson={resolvePerson}
                                  onSelectPerson={onSelectPerson}
                                  avatarBg={avatarBg}
                                />
                              </div>
                            );
                          }

                          return (
                            <React.Fragment key={personKey}>
                              {personBtn}
                            </React.Fragment>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </article>
        );
      })}
    </div>
  );
};

export default TeamViewsAccountBoard;
